import classnames from 'classnames';

import componentMap from '../../components';
import withErrorBoundary from '../ErrorBoundary';
import ErrorBoundary from '../ErrorBoundary/ErrorBoundary';
import { MBDContextProvider } from '../common/MBD/useMBDContext';

import type { FeatureContainerProps } from 'common-types/types/FeatureContainerProps';

import STYLES from './FeatureContainer.module.scss';

const FeatureContainer = (props: FeatureContainerProps): JSX.Element => {
  const { advert, components, hasStrevda, headerComponents } = props;

  let content = (
    <>
      {components.map(
        ({
          componentConfigName,
          componentProps,
          dataIdConfigSource,
          extraClasses,
          id,
          isCriticalComponent,
          isNoMargin,
          name,
          shadowingComponent,
          vertical,
        }) => {
          const PageComponent = componentMap[name];
          const componentIdentifier = id || name;
          if (!PageComponent) return null;
          return (
            <ErrorBoundary
              key={componentIdentifier}
              componentName={`feature-container-component.${name}`}
              metadata={{ isCriticalComponent }}
            >
              <div
                id={componentIdentifier}
                data-id-config-source={
                  dataIdConfigSource || 'static--not-platform-component'
                }
                data-id-shadowing={shadowingComponent}
                className={classnames(extraClasses, {
                  [STYLES.FeatureItem]: !isNoMargin,
                  [STYLES.FeatureItemNoMargin]: isNoMargin,
                })}
              >
                <MBDContextProvider
                  rawComponentName={componentConfigName}
                  vertical={vertical}
                >
                  <PageComponent {...componentProps} />
                </MBDContextProvider>
              </div>
            </ErrorBoundary>
          );
        },
      )}
    </>
  );

  if (advert && componentMap[advert.name]) {
    const PageComponent = componentMap[advert.name];
    const componentIdentifier = advert.id || advert.name;
    content = (
      <ErrorBoundary
        key={componentIdentifier}
        componentName={`feature-container-component.${advert.name}`}
      >
        <div id={componentIdentifier} className={STYLES.FeatureItem}>
          <PageComponent {...advert.componentProps}>{content}</PageComponent>
        </div>
      </ErrorBoundary>
    );
  }

  return (
    <div className={STYLES.FeatureContainer}>
      {headerComponents && (
        <div>
          {headerComponents.map(
            ({
              componentConfigName,
              componentProps,
              dataIdConfigSource,
              id,
              isCriticalComponent,
              isFullWidth,
              name,
              vertical,
            }) => {
              const PageComponent = componentMap[name];
              const componentIdentifier = id || name;
              if (!PageComponent) return null;
              return (
                <ErrorBoundary
                  key={componentIdentifier}
                  componentName={`feature-container-component.${name}`}
                  metadata={{ isCriticalComponent }}
                >
                  <div
                    id={componentIdentifier}
                    data-id-config-source={
                      dataIdConfigSource || 'static--not-platform-component'
                    }
                    className={
                      isFullWidth ? STYLES.FullWidth : STYLES.ItemWidth
                    }
                  >
                    <MBDContextProvider
                      rawComponentName={componentConfigName}
                      vertical={vertical}
                    >
                      <PageComponent {...componentProps} />
                    </MBDContextProvider>
                  </div>
                </ErrorBoundary>
              );
            },
          )}
        </div>
      )}
      <div
        className={hasStrevda ? STYLES.MainSectionWithAD : STYLES.MainSection}
      >
        <div className={STYLES.Main} id="feature-container-main">
          {content}
        </div>
        {hasStrevda ? (
          <aside id="strevda-mpu-wrapper" className={STYLES.Sidebar} />
        ) : null}
      </div>
    </div>
  );
};

export default withErrorBoundary(
  FeatureContainer,
  'feature-container-component',
);
